import { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import CommentOutlined from '@mui/icons-material/CommentOutlined';
import Constants from 'style/constants';
import OrganisationBreadCrumb from 'shared/components/bread-crumb/organisation-bread-crumb/organisation-bread-crumb';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import MonitoringIcon from 'shared/components/monitoring/monitoring-icon';
import { MonitoringType } from 'shared/components/monitoring/monitoring-type';
import PageHeader from 'shared/components/page-header/page-header';
import PopUp from 'shared/components/pop-up/pop-up';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import { TableOverflowMenuItem } from 'shared/components/table/hooks';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';
import { findUpstreamParentBranch } from 'shared/features/organisation/models/organisation-tree-node';
import { useOrganisationUnitTreeContext } from 'shared/features/organisation/providers/organisation-unit-tree-provider';

import GatewayOverviewRecord from 'features/gateway/domain/models/gateway-overview-record';
import { GatewayStatus } from 'features/gateway/domain/models/gateway-status';
import GatewayDetailsPopup from 'features/gateway/views/gateway-details/gateway-details-popup.component';
import GatewayFilters from 'features/gateway/views/gateway-filters';
import styles from 'features/gateway/views/gateways.module.scss';
import IntegrationsNavigation from 'features/integrations/views/integrations-navigation';
import { useGatewaysContextProvider } from '../context/gateway-provider';

const Gateways = (): JSX.Element => {
  const { t } = useTranslation('gateway');
  const { hasPermission } = usePermissionAuthorization();
  const { organisationUnitTree } = useOrganisationUnitTreeContext();

  const {
    gatewayDetailsHook,
    gatewayFilterHook,
    openDetailsInCreationMode,
    openDetailsInEditingModeAndRefresh,
    openDetailsInViewingModeAndRefresh,
    deleteGateway,
  } = useGatewaysContextProvider();

  const getOrganisationBreadCrumb = (gateway: GatewayOverviewRecord): JSX.Element => {
    if (gateway.organisationUnitId && organisationUnitTree) {
      const upstreamParentBranch = findUpstreamParentBranch(organisationUnitTree, gateway.organisationUnitId, []);
      if (upstreamParentBranch) {
        return (
          <OrganisationBreadCrumb
            rootNodeId={gateway.organisationUnitId}
            upstreamParentBranch={upstreamParentBranch}
            data-testId="organisationBreadCrumb"
          />
        );
      }
    }
    return <></>;
  };

  const columns: ColumnDefinition<GatewayOverviewRecord, keyof GatewayOverviewRecord>[] = [
    { key: 'name', label: t('nameHeaderLabel') },
    {
      key: 'type',
      label: t('typeHeaderLabel'),
      renderCustomContentProvider: (gateway) => <>{t(`details.settings.type.${gateway.type}`)}</>,
    },
    {
      key: 'organisationUnitId',
      label: t('organisationUnitLabel'),
      renderCustomContentProvider: getOrganisationBreadCrumb,
      disableSort: true,
      tableCellProps: {
        classes: { root: styles.listOrganisationBreadCrumb },
      },
    },
    {
      key: 'note',
      label: '',
      renderCustomContentProvider: (gateway) => <div className={styles.iconRow}>{getIcons(gateway)}</div>,
      disableSort: true,
      tableCellProps: {
        align: 'right',
        width: '24px',
      },
    },
  ];

  const getIcons = (gateway: GatewayOverviewRecord): JSX.Element[] => {
    const result: JSX.Element[] = [];

    result.push(
      <CommentOutlined
        data-testid="noteIcon"
        key={'note-icon'}
        sx={{
          width: 16,
          height: 16,
          color: Constants.Colors.onSurfaceVariant,
          visibility: gateway.note ? 'visible' : 'hidden',
        }}
      />,
    );

    if (gateway.status === GatewayStatus.offline) {
      result.push(
        <MonitoringIcon
          key={`monitoring-icon-${MonitoringType.GatewayOffline}`}
          type={MonitoringType.GatewayOffline}
          withTooltip={true}
          visible={true}
          size={16}
        />,
      );
    } else if (gateway.status === GatewayStatus.serviceOfflineOnly) {
      result.push(
        <MonitoringIcon
          key={`monitoring-icon-${MonitoringType.GatewayServiceOffline}`}
          type={MonitoringType.GatewayServiceOffline}
          withTooltip={true}
          visible={true}
          size={16}
        />,
      );
    } else if (gateway.status === GatewayStatus.passiveOfflineOnly) {
      result.push(
        <MonitoringIcon
          key={`monitoring-icon-${MonitoringType.PassiveGatewayOffline}`}
          type={MonitoringType.PassiveGatewayOffline}
          withTooltip={true}
          visible={true}
          size={16}
        />,
      );
    } else if (gateway.status === GatewayStatus.online) {
      result.push(
        <MonitoringIcon
          key={`monitoring-icon-${MonitoringType.GatewayOnline}`}
          type={MonitoringType.GatewayOnline}
          withTooltip={true}
          visible={true}
          size={16}
        />,
      );
    } else if (gateway.status === GatewayStatus.notMonitored) {
      result.push(
        <MonitoringIcon
          key={`monitoring-icon-${MonitoringType.GatewayNotMonitored}`}
          type={MonitoringType.GatewayNotMonitored}
          withTooltip={true}
          visible={true}
          size={16}
        />,
      );
    }

    return result;
  };

  const getOverflowMenuItems = (): Array<TableOverflowMenuItem<GatewayOverviewRecord>> | undefined => {
    const menuItems: Array<TableOverflowMenuItem<GatewayOverviewRecord>> = [
      {
        label: t('overflowMenu.view'),
        action: (gateway) => openDetailsInViewingModeAndRefresh(gateway),
      },
    ];

    if (hasPermission(Permission.UpdateGateways)) {
      menuItems.push({
        label: t('overflowMenu.edit'),
        action: (gateway) => openDetailsInEditingModeAndRefresh(gateway),
      });
    }
    if (hasPermission(Permission.DeleteGateways)) {
      menuItems.push({
        label: t('overflowMenu.delete'),
        action: (gateway) => deleteGateway(gateway),
      });
    }

    return menuItems;
  };

  return (
    <div className={styles.container}>
      <PageHeader
        title={t('pageTitle')}
        addBtnVisible={gatewayFilterHook.gatewaysResponseIsSuccess && hasPermission(Permission.CreateGateways)}
        onAdd={openDetailsInCreationMode}
        navigationComponent={<IntegrationsNavigation />}
        filterComponent={
          <div className={styles.filterComponentContainer}>
            <GatewayFilters />
          </div>
        }
      />
      {gatewayFilterHook.gatewaysResponseIsLoading && <LoadingIndicator />}
      {gatewayFilterHook.gatewaysResponseIsSuccess &&
        gatewayFilterHook.gatewaysResponse &&
        !gatewayFilterHook.gatewaysResponseIsLoading && (
          <Table
            data={gatewayFilterHook.gatewaysResponse.gateways.map<RowItem<GatewayOverviewRecord>>((gateway) => ({ data: gateway }))}
            columns={columns}
            onItemClick={(gateway: GatewayOverviewRecord) => openDetailsInViewingModeAndRefresh(gateway)}
            overflowMenuOptions={getOverflowMenuItems()}
            rowIdentifier={(gateway: GatewayOverviewRecord) => gateway.id}
            initialOrderBy={gatewayFilterHook.table.sortFromSessionStorage?.property}
            initialOrderDirection={gatewayFilterHook.table.sortFromSessionStorage?.isAscending ? 'asc' : 'desc'}
            useBuiltInSorter={true}
            pagination={{
              count: gatewayFilterHook.gatewaysResponse.total,
              onPageChanged: gatewayFilterHook.table.handleOnPageChanged,
              rowsPerPage: gatewayFilterHook.table.currentRowsPerPage,
              page: gatewayFilterHook.table.currentPage,
              onRowsPerPageChanged: gatewayFilterHook.table.handleOnRowsPerPageChanged,
            }}
            onSortChanged={gatewayFilterHook.table.handleOnSortChanged}
          />
        )}
      {gatewayDetailsHook.isDetailsOpen && <GatewayDetailsPopup />}
      <PopUp
        isOpen={gatewayDetailsHook.isDeleteConfirmationPopupOpen}
        title={t('deleteGatewayConfirmation.title', {
          gatewayName: gatewayDetailsHook.gatewayPendingDeletion?.name,
        })}
        body={t('deleteGatewayConfirmation.bodyText')}
        primaryButtonText={t('deleteGatewayConfirmation.confirmButton')}
        secondaryButtonText={t('deleteGatewayConfirmation.cancelButton')}
        handleOnClose={gatewayDetailsHook.closeDeleteConfirmationPopup}
        secondaryButtonAction={gatewayDetailsHook.closeDeleteConfirmationPopup}
        primaryButtonAction={() => gatewayDetailsHook.deleteConfirmationAction?.()}
        isLoadingPrimaryAction={gatewayDetailsHook.mutationIsLoading}
      />
      <PopUp
        isOpen={gatewayDetailsHook.isDeleteErrorPopupOpen}
        title={t('deleteGatewayError.title', {
          gatewayName: gatewayDetailsHook.gatewayPendingDeletion?.name,
        })}
        body={t(`deleteGatewayError.${gatewayDetailsHook.deleteErrorPopupTextKey}`, {
          gatewayName: gatewayDetailsHook.gatewayPendingDeletion?.name,
        })}
        primaryButtonText={t('deleteGatewayError.confirmButton')}
        handleOnClose={gatewayDetailsHook.closeDeleteErrorPopup}
        primaryButtonAction={gatewayDetailsHook.closeDeleteErrorPopup}
      />
    </div>
  );
};

export default Gateways;
